import dayjs from 'dayjs/esm/index';
import isoweek from 'dayjs/esm/plugin/isoWeek';
import quarterOfYear from 'dayjs/esm/plugin/quarterOfYear';

// Module scope, not the constructor: the static factories below evaluate
// startOf('isoWeek') / startOf('quarter') as constructor *arguments*, so they run
// before any extend() inside the constructor body could register the plugins.
dayjs.extend(quarterOfYear);
dayjs.extend(isoweek);

export class DateRange {
    toDate: Date;
    fromDate: Date;
    duration: number;

    constructor(fromDate: string | Date | dayjs.Dayjs, toDate: string | Date | dayjs.Dayjs) {
        if (toDate) {
            this.toDate = dayjs(toDate).toDate();
        }
        if (fromDate) {
            this.fromDate = dayjs(fromDate).toDate();
        }
        if (this.fromDate && this.toDate) {
            this.duration = this.calcDuration();
        }
    }

    static forPast90Days(): DateRange {
        return new DateRange(dayjs().subtract(90, 'day').toDate(), dayjs().subtract(1, 'day').toDate());
    }

    static forPast60Days(): DateRange {
        return new DateRange(dayjs().subtract(60, 'day').toDate(), dayjs().subtract(1, 'day').toDate());
    }

    static forPast30Days(): DateRange {
        return new DateRange(dayjs().subtract(30, 'day').toDate(), dayjs().subtract(1, 'day').toDate());
    }

    static forPast7Days(): DateRange {
        return new DateRange(dayjs().subtract(7, 'day').toDate(), dayjs().subtract(1, 'day').toDate());
    }

    static forNext9Weeks(): DateRange {
        return new DateRange(dayjs().startOf('isoWeek').toDate(), dayjs().startOf('isoWeek').add(10, 'weeks').add(-1, 'days').toDate());
    }

    static forNext8Weeks(): DateRange {
        return new DateRange(dayjs().startOf('isoWeek').toDate(), dayjs().startOf('isoWeek').add(9, 'weeks').add(-1, 'days').toDate());
    }

    static forSeason(date): DateRange {
        return new DateRange(date.startOf('quarter').subtract(1, 'month').toDate(), date.endOf('quarter').subtract(1, 'month').toDate());
    }

    static forCurrentYear(): DateRange {
        return new DateRange(dayjs().startOf('year').toDate(), dayjs().endOf('year').toDate());
    }

    static forPastYear(): DateRange {
        return new DateRange(dayjs().subtract(1, 'year').startOf('year').toDate(), dayjs().subtract(1, 'year').endOf('year').toDate());
    }

    static forYearToDate(date: string | Date | dayjs.Dayjs = dayjs().subtract(1, 'day')): DateRange {
        return new DateRange(dayjs().startOf('year').toDate(), dayjs(date).startOf('day').toDate());
    }

    calcDuration(): number {
        return dayjs(this.toDate).diff(this.fromDate, 'days') + 1;
    }

    /**
     * StartDate/EndDate are calendar dates, not instants: the API binds them to a plain
     * DateTime (Common/Models/DateRange.cs) and applies no timezone coercion, so whatever
     * `YYYY-MM-DD` we send here IS the day the query runs for.
     *
     * FTK-7484: this used to be `toISOString().substring(0, 10)`, which re-reads a
     * local-midnight Date in UTC. At a positive UTC offset that lands on the previous day,
     * so a user in Madrid asking for May 9 - Aug 4 silently queried May 8 - Aug 3 while a
     * user in New York got the range they picked. The `isTimezoneOffset` opt-in that
     * corrected it is removed rather than defaulted-on: 25 of its 38 call sites never
     * passed it, including every endpoint behind Discovery's "Breakdown by Website".
     *
     * Formatting through dayjs reads the LOCAL calendar fields, which also fixes the ranges
     * whose end is `23:59:59.999` local (`forCurrentYear` / `forPastYear` / `forSeason`) —
     * those overflowed FORWARD a day under `toISOString()` for every timezone west of
     * Greenwich — and anchors the `forPastNDays` presets, which carry a time of day, to the
     * user's own local yesterday.
     *
     * `DateSerializer.serialize` and the People `ymd` helper read the same local fields but
     * emit `yyyy-M-d`, and the People one is unpadded deliberately, to keep its query string
     * byte-identical to the AngularJS fixtures the parity harness replays. Same contract,
     * different string — so do not collapse the three.
     */
    toParams() {
        return {
            StartDate: DateRange.toLocalYmd(this.fromDate),
            EndDate: DateRange.toLocalYmd(this.toDate)
        };
    }

    private static toLocalYmd(date: Date): string {
        // Guarded because `dayjs(undefined)` is NOW: the constructor only assigns fromDate /
        // toDate when they are truthy, and the ranges built from an Experiment can carry a
        // null end_date, so an absent date reaching format() would silently serialize as
        // today. `toISOString()` threw on both absent and Invalid dates before FTK-7484;
        // keep that loud rather than shipping a plausible wrong day.
        if (!date || Number.isNaN(date.getTime())) {
            throw new TypeError(`DateRange.toParams(): ${date} is not a usable date`);
        }
        return dayjs(date).format('YYYY-MM-DD');
    }
}

